import type { FeatureFlagKey } from './registry'

/**
 * Pure feature flag resolver. No I/O, no SolidJS, no PostHog imports.
 *
 * Resolution order (highest priority wins):
 *   1. override (query param / sessionStorage) — absolute, can set true OR false
 *   2. PostHog flag — evaluated server-side against the current session's
 *      "artist" group context (see posthog-bridge.posthogSetArtistGroup)
 *   3. Declared default from the registry
 *
 * Per-artist targeting is handled by PostHog Group Analytics, not by this
 * function. When the resolver sees `posthogFlags[key] === true`, that already
 * reflects the group-targeted evaluation — no `:slug` suffix needed.
 */

export type ResolverInputs = {
  readonly key: FeatureFlagKey
  readonly overrides: Partial<Record<FeatureFlagKey, boolean>>
  readonly posthogFlags: Readonly<Record<string, boolean>>
  readonly defaults: Readonly<Record<FeatureFlagKey, boolean>>
}

export function resolve({ key, overrides, posthogFlags, defaults }: ResolverInputs): boolean {
  // 1. Override wins absolutely (can be true or false).
  if (key in overrides) {
    return overrides[key]!
  }

  // 2. PostHog flag — group-targeted evaluation already applied server-side.
  if (posthogFlags[key] === true) {
    return true
  }

  // 3. Declared default.
  return defaults[key]
}
